import { api } from '@/api/http';
import { LogoLoader } from '@/components/logo-loader';
import { $features, $setFeatures } from '@/entities/features-entity';
import { $nickname, $profile, $setNickname, $setProfile } from '@/entities/profile-entity';
import { mockProfile } from '@/mocks/fixtures';
import { isDevMode } from '@/lib/is-dev-mode';
import { clearSessionKeeper, startSessionKeeper } from '@/lib/session-keeper';
import { $setConnectionPhase, isServerUnavailableStatus, $connectionPhase } from '@/entities/connection-entity';
import { seedDevData } from '@/mocks/seed-dev-data';
import AuthTelegramGate from '@/pages/auth/auth-telegram-gate';
import { ServerStatusOverlay } from '@/components/server-status-overlay';
import axios from 'axios';
import { useUnit } from 'effector-react';
import { useEffect, useState, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';

if (isDevMode) {
	seedDevData();
}

type BootstrapState = 'loading' | 'ready' | 'no_telegram' | 'unavailable';

export default function AuthProvider({ children }: PropsWithChildren) {
	const [setNickname, nickname, profile, setProfile, setFeatures, setConnectionPhase, connectionPhase] = useUnit([
		$setNickname,
		$nickname,
		$profile,
		$setProfile,
		$setFeatures,
		$setConnectionPhase,
		$connectionPhase,
	]);
	const navigate = useNavigate();
	const isPublic = location.pathname === '/auth';
	const [bootstrapState, setBootstrapState] = useState<BootstrapState>('loading');

	useEffect(() => {
		let cancelled = false;

		async function bootstrapAuth() {
			try {
				const res = await api.get('/me', { suppressConnectionOverlay: true });
				if (cancelled) return;
				setProfile(res.data.profile);
				setNickname(res.data.profile.nickname);
				if (res.data.features) {
					setFeatures(res.data.features);
				}
				if (typeof res.data.sessionExpiresIn === 'number') {
					startSessionKeeper(res.data.sessionExpiresIn, res.data.sessionRefreshBeforeSec);
				}
				setBootstrapState('ready');
			} catch (error) {
				if (cancelled) return;

				if (axios.isAxiosError(error)) {
					const status = error.response?.status;
					if (!status || isServerUnavailableStatus(status) || error.code === 'ERR_NETWORK') {
						if (isDevMode) {
							setProfile(mockProfile);
							setNickname(mockProfile.nickname);
							setFeatures({ ...$features.getState(), landingsEnabled: true });
							setBootstrapState('ready');
							return;
						}
						setConnectionPhase('unavailable');
						setBootstrapState('unavailable');
						return;
					}
				}

				const initData = isDevMode ? '' : window.Telegram?.WebApp?.initData;
				if (!isDevMode && !initData) {
					setBootstrapState('no_telegram');
					return;
				}

				try {
					const res = await api.post('/get-profile-name', { initData: initData ?? '' });
					if (!cancelled && res.data.nickname) {
						setNickname(res.data.nickname);
					}
				} catch {
					// nickname stays empty
				} finally {
					if (!cancelled) setBootstrapState('ready');
				}
			}
		}

		void bootstrapAuth();

		return () => {
			cancelled = true;
			clearSessionKeeper();
		};
	}, [setNickname, setProfile, setFeatures, setConnectionPhase]);

	useEffect(() => {
		if (bootstrapState !== 'unavailable' || connectionPhase !== 'online') return;

		let cancelled = false;

		async function retryBootstrap() {
			try {
				const res = await api.get('/me', { suppressConnectionOverlay: true });
				if (cancelled) return;
				setProfile(res.data.profile);
				setNickname(res.data.profile.nickname);
				if (res.data.features) {
					setFeatures(res.data.features);
				}
				if (typeof res.data.sessionExpiresIn === 'number') {
					startSessionKeeper(res.data.sessionExpiresIn, res.data.sessionRefreshBeforeSec);
				}
				setBootstrapState('ready');
			} catch (error) {
				if (cancelled) return;
				if (axios.isAxiosError(error)) {
					const status = error.response?.status;
					if (!status || isServerUnavailableStatus(status) || error.code === 'ERR_NETWORK') {
						setConnectionPhase('unavailable');
						return;
					}
				}
				setBootstrapState('ready');
			}
		}

		void retryBootstrap();

		return () => {
			cancelled = true;
		};
	}, [bootstrapState, connectionPhase, setConnectionPhase, setFeatures, setNickname, setProfile]);

	useEffect(() => {
		if (nickname && !profile && !isPublic && bootstrapState === 'ready') {
			navigate('/auth');
		}
	}, [nickname, profile, isPublic, bootstrapState, navigate]);

	if (bootstrapState === 'loading') {
		return (
			<div className='flex h-full min-h-0 w-full flex-col overflow-hidden'>
				<div className='flex flex-1 items-center justify-center'>
					<LogoLoader />
				</div>
			</div>
		);
	}

	if (bootstrapState === 'unavailable') {
		return <ServerStatusOverlay phase='unavailable' />;
	}

	if (bootstrapState === 'no_telegram') {
		return <AuthTelegramGate />;
	}

	if (!nickname) {
		return <AuthTelegramGate message='Не удалось определить аккаунт. Попробуйте открыть приложение заново.' />;
	}

	if (nickname && !profile && !isPublic && bootstrapState === 'ready') {
		return <></>;
	}

	return <div className='flex h-full min-h-0 w-full flex-col overflow-hidden'>{children}</div>;
}
